import { TanStackDevtools } from '@tanstack/react-devtools'
import {
  ErrorComponent,
  Outlet,
  createRootRouteWithContext,
  useMatches,
} from '@tanstack/react-router'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import type { ErrorComponentProps } from '@tanstack/react-router'

import type { QueryClient } from '@tanstack/react-query'
import TanStackQueryDevtools from '@/integrations/tanstack-query/devtools'

import { Footer } from '@/components/layout/Footer'
import { Header } from '@/components/layout/Header'
import { CookieConsent } from '@/components/cookie-consent'
import { CookieConsentProvider } from '@/contexts/CookieConsentContext'
import { LoginModalProvider } from '@/contexts/LoginModalContext'
import { SilentRedirectError } from '@/utils/apiUtils'

interface MyRouterContext {
  queryClient: QueryClient
}

const RootComponent = () => {
  const matches = useMatches()
  const lastMatch = matches[matches.length - 1]
  const isLight =
    (lastMatch.staticData as { headerLight?: boolean }).headerLight ?? false
  const shouldShowDevtools = import.meta.env.DEV

  return (
    <CookieConsentProvider>
      <LoginModalProvider>
        <div className="flex flex-col min-h-screen">
          <Header isLight={isLight} />
          <main className="pt-18 flex-1">
            <Outlet />
          </main>
          {shouldShowDevtools && (
            <TanStackDevtools
              config={{
                position: 'bottom-right',
              }}
              plugins={[
                {
                  name: 'Tanstack Router',
                  render: <TanStackRouterDevtoolsPanel />,
                },
                TanStackQueryDevtools,
              ]}
            />
          )}
          <Footer />
        </div>
      </LoginModalProvider>
      <CookieConsent />
    </CookieConsentProvider>
  )
}

const RootErrorComponent = (props: ErrorComponentProps) => {
  // Une redirection (window.location.href) est déjà déclenchée côté
  // apiUtils (secureRequest) : on n'affiche rien plutôt que le fallback
  // générique "Something went wrong!", le temps que le navigateur termine
  // la navigation.
  if (props.error instanceof SilentRedirectError) {
    return null
  }

  return <ErrorComponent {...props} />
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: RootComponent,
  errorComponent: RootErrorComponent,
})
